import React from "react";
import "./Gpmswhychoose.css";

/* ============================================================
   CONTENT  (client ka content – yahin se edit karo)
   ============================================================ */
const HEAD = {
  badge: "WHY CHOOSE GPMS",
  title: "Why Choose GPMS?",
  subtitle: "More Than Business Intelligence",
  intro: [
    "Traditional reporting systems primarily focus on presenting historical information.",
    "GPMS combines operational analytics with computer vision and predictive intelligence to provide a broader understanding of current operations and potential future challenges.",
  ],
};

const COMPARISON = [
  ["Primarily historical reporting", "Live and historical operational analytics"],
  ["Separate system reports", "Unified multi-system analytical view"],
  ["Manual identification workflows", "AI-assisted vehicle and container recognition"],
  ["Reactive gate congestion analysis", "Predictive gate congestion intelligence"],
  ["Historical dwell reporting", "Container dwell risk forecasting"],
  [
    "Manual interpretation of multiple reports",
    "Consolidated operational decision-support dashboards",
  ],
];

const BENEFITS_TITLE = "Business Benefits";

const BENEFITS = [
  {
    title: "Improved Operational Visibility",
    desc: "Gain a consolidated understanding of terminal activities across multiple operational systems.",
    icon: "eye",
  },
  {
    title: "Proactive Decision-Making",
    desc: "Use predictive intelligence to anticipate congestion and container dwell-related risks.",
    icon: "trend",
  },
  {
    title: "Enhanced Gate Efficiency",
    desc: "Improve planning through truck turnaround predictions and AI-assisted identification.",
    icon: "zap",
  },
  {
    title: "Better Yard Capacity Planning",
    desc: "Identify potential long-dwell containers and support more efficient utilization of terminal storage space.",
    icon: "box",
  },
  {
    title: "Reduced Manual Effort",
    desc: "Minimize repetitive reporting and identification tasks through integrated analytics and computer vision.",
    icon: "refresh",
  },
  {
    title: "Greater Operational Transparency",
    desc: "Access consistent performance information for monitoring, planning, and management reviews.",
    icon: "shield",
  },
];

/* ============================================================
   ICONS
   ============================================================ */
const ICONS = {
  eye: (
    <>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </>
  ),
  trend: (
    <>
      <path d="M22 7 13.5 15.5 8.5 10.5 2 17" />
      <path d="M16 7h6v6" />
    </>
  ),
  zap: <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />,
  box: (
    <>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </>
  ),
  refresh: (
    <>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </>
  ),
  shield: (
    <>
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      <path d="m9 12 2 2 4-4" />
    </>
  ),
};

const Icon = ({ name }) => (
  <svg
    viewBox="0 0 24 24"
    width="28"
    height="28"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICONS[name]}
  </svg>
);

/* ============================================================
   SECTION
   ============================================================ */
const GPMSWhyChoose = () => (
  <section className="gw">
    <div className="gw__container">
      {/* ---------- heading ---------- */}
      <header className="gw__head">
        <span className="gw__pill">{HEAD.badge}</span>
        <h2 className="gw__title">{HEAD.title}</h2>
        <p className="gw__subtitle">{HEAD.subtitle}</p>
        <span className="gw__underline" />
        {HEAD.intro.map((p, i) => (
          <p key={i} className="gw__intro">
            {p}
          </p>
        ))}
      </header>

      {/* ---------- comparison ---------- */}
      <div className="gw__table" role="table" aria-label="Traditional BI versus GPMS">
        <div className="gw__row gw__row--head" role="row">
          <div className="gw__cell gw__cell--bi" role="columnheader">
            Traditional BI
          </div>
          <div className="gw__cell gw__cell--gpms" role="columnheader">
            GPMS
          </div>
        </div>

        {COMPARISON.map(([bi, gpms]) => (
          <div className="gw__row" role="row" key={bi}>
            <div className="gw__cell gw__cell--bi" role="cell">
              <span className="gw__mark gw__mark--no" aria-hidden="true">
                ✕
              </span>
              <span>{bi}</span>
            </div>
            <div className="gw__cell gw__cell--gpms" role="cell">
              <span className="gw__mark gw__mark--yes" aria-hidden="true">
                ✓
              </span>
              <span>{gpms}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ---------- benefits ---------- */}
      <div className="busi-ifile-wrapper">
      <div className="gw__benefits-head">
        <h3 className="gw__benefits-title">{BENEFITS_TITLE}</h3>
        <span className="gw__underline gw__underline--center" />
      </div>

      <div className="gw__grid">
        {BENEFITS.map((b, i) => (
          <article className="gw__card" key={b.title}>
            <span className="gw__card-num" aria-hidden="true">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="gw__icon">
              <Icon name={b.icon} />
            </span>
            <h4 className="gw__card-title">{b.title}</h4>
            <p className="gw__card-desc">{b.desc}</p>
          </article>
        ))}
      </div>
      </div>
    </div>
  </section>
);

export default GPMSWhyChoose;